import { useEffect, useRef, useState } from "react"

import {
  Button,
  Checkbox,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@coval/ui"

import { updateScheduleSlackChannels } from "../utils/generatedNotebooks"
import type { NotebookRow } from "../utils/notebookSeries"
import {
  fetchNotebookSlackCatalog,
  loadMoreNotebookSlackChannels,
  saveWorkspaceChannelNotebookReady,
  workspaceNotebookSlackChannels,
  type NotebookSlackCatalog,
} from "../utils/notebookSlack"

export function NotebookSlackDialog({
  open,
  row,
  organizationSlug,
  workspaceId,
  initialCatalog,
  onOpenChange,
  onSaved,
}: {
  open: boolean
  row: NotebookRow | null
  organizationSlug: string
  workspaceId: string
  initialCatalog: NotebookSlackCatalog | null
  onOpenChange: (open: boolean) => void
  onSaved: (args: {
    scheduleId?: string
    slackChannelIds?: string[] | null
    catalog?: NotebookSlackCatalog | null
  }) => void
}) {
  const [catalog, setCatalog] = useState<NotebookSlackCatalog | null>(
    initialCatalog,
  )
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [acknowledged, setAcknowledged] = useState(false)
  const [newChannelId, setNewChannelId] = useState("")
  const [selectedChannels, setSelectedChannels] = useState<Set<string>>(
    new Set(),
  )
  // The schedule's saved recipients when the dialog opened; a channel whose
  // binding was removed since still needs a checkbox so it can be cleared.
  const [savedChannelIds, setSavedChannelIds] = useState<string[]>([])
  const initialRef = useRef({ initialCatalog, row })
  initialRef.current = { initialCatalog, row }

  useEffect(() => {
    if (!open) {
      return
    }
    const controller = new AbortController()
    const seedSelection = (source: NotebookSlackCatalog | null) => {
      const readyIds = (
        workspaceNotebookSlackChannels(source, workspaceId) ?? []
      )
        .filter((ch) => ch.notebookReady)
        .map((ch) => ch.id)
      const savedIds = initialRef.current.row?.schedule?.slack_channel_ids
      setSavedChannelIds(Array.isArray(savedIds) ? savedIds : [])
      setSelectedChannels(
        new Set(Array.isArray(savedIds) ? savedIds : readyIds),
      )
    }
    setError(null)
    setAcknowledged(false)
    setNewChannelId("")
    // Seed from the list's catalog so a failed reload never saves an empty draft.
    seedSelection(initialRef.current.initialCatalog)
    setLoading(true)
    void fetchNotebookSlackCatalog(organizationSlug, controller.signal)
      .then((loaded) => {
        if (controller.signal.aborted) {
          return
        }
        const effective = loaded ?? initialRef.current.initialCatalog
        setCatalog(effective)
        seedSelection(effective)
      })
      .catch((failure: unknown) => {
        if (!controller.signal.aborted) {
          setError(
            failure instanceof Error
              ? failure.message
              : "Could not load Slack channels. Close and reopen this dialog to retry.",
          )
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setLoading(false)
        }
      })
    return () => controller.abort()
  }, [open, organizationSlug, workspaceId])

  const workspaceBindings =
    catalog?.bindings.filter(
      (b) => b.workspace_id === workspaceId && !b.is_shared,
    ) ?? []
  const boundChannelIds = new Set(
    catalog?.bindings.map((b) => b.channel_id) ?? [],
  )
  const availableToConnect =
    catalog?.channels.filter(
      (ch) => !ch.is_shared && !boundChannelIds.has(ch.id),
    ) ?? []
  const channelsById = new Map(
    (catalog?.channels ?? []).map((ch) => [ch.id, ch]),
  )
  const workspaceBindingIds = new Set(
    workspaceBindings.map((b) => b.channel_id),
  )
  const disconnectedSavedIds = savedChannelIds.filter(
    (id) => !workspaceBindingIds.has(id),
  )
  const notebookReadyAvailable = catalog?.notebook_ready_available === true
  const moreChannelsAvailable = Boolean(catalog?.next_channel_cursor)

  const toggleChannelSelection = (channelId: string, checked: boolean) => {
    setSelectedChannels((prev) => {
      const next = new Set(prev)
      if (checked) {
        next.add(channelId)
      } else {
        next.delete(channelId)
      }
      return next
    })
  }

  const loadMoreChannels = async () => {
    if (!catalog || loadingMore) {
      return
    }
    setLoadingMore(true)
    setError(null)
    try {
      const nextCatalog = await loadMoreNotebookSlackChannels(
        organizationSlug,
        catalog,
      )
      setCatalog(nextCatalog)
      onSaved({ catalog: nextCatalog })
    } catch (err: unknown) {
      setError(
        err instanceof Error ? err.message : "Could not load Slack channels.",
      )
    } finally {
      setLoadingMore(false)
    }
  }

  const connectChannel = async () => {
    if (!catalog || !newChannelId || !acknowledged || saving) {
      return
    }
    setSaving(true)
    setError(null)
    try {
      const created = await saveWorkspaceChannelNotebookReady({
        organizationSlug,
        workspaceId,
        channelId: newChannelId,
        enableNotebookReady: true,
      })
      const nextCatalog: NotebookSlackCatalog = {
        ...catalog,
        bindings: [
          ...catalog.bindings.filter((b) => b.channel_id !== newChannelId),
          created,
        ],
      }
      setCatalog(nextCatalog)
      setSelectedChannels((prev) => new Set([...prev, newChannelId]))
      setNewChannelId("")
      onSaved({ catalog: nextCatalog })
    } catch (err: unknown) {
      setError(
        err instanceof Error ? err.message : "Could not connect Slack channel.",
      )
    } finally {
      setSaving(false)
    }
  }

  const saveSeriesSlack = async () => {
    if (saving) {
      return
    }
    setSaving(true)
    setError(null)
    try {
      let nextCatalog = catalog
      if (nextCatalog) {
        for (const binding of workspaceBindings) {
          const wantReady = selectedChannels.has(binding.channel_id)
          const isReady = binding.enabled_surfaces.includes("notebook_ready")
          if (wantReady && !isReady) {
            if (!notebookReadyAvailable) {
              setError(
                "Scheduled notebook notifications are not available for this Slack workspace yet.",
              )
              return
            }
            if (!acknowledged) {
              setError(
                "Acknowledge the channel audience policy before enabling a new Slack channel.",
              )
              setSaving(false)
              return
            }
            const updated = await saveWorkspaceChannelNotebookReady({
              organizationSlug,
              workspaceId,
              channelId: binding.channel_id,
              enableNotebookReady: true,
              existing: binding,
            })
            nextCatalog = {
              ...nextCatalog,
              bindings: nextCatalog.bindings.map((item) =>
                item.channel_id === binding.channel_id ? updated : item,
              ),
            }
            setCatalog(nextCatalog)
          }
        }
      }
      if (row?.schedule) {
        const scheduleId = row.schedule.id
        const selected = [...selectedChannels]
        await updateScheduleSlackChannels(
          organizationSlug,
          workspaceId,
          scheduleId,
          selected,
          row.schedule.updated_at ?? null,
        )
        onSaved({
          scheduleId,
          slackChannelIds: selected,
          catalog: nextCatalog,
        })
      } else {
        onSaved({ catalog: nextCatalog })
      }
      onOpenChange(false)
    } catch (err: unknown) {
      setError(
        err instanceof Error
          ? err.message
          : "Could not save Slack notification settings.",
      )
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {row ? `Slack notifications · ${row.title}` : "Slack notifications"}
          </DialogTitle>
          <DialogDescription>
            Choose which Slack channels receive a summary and link when a new
            scheduled edition is ready.
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-4">
          {loading ? (
            <p role="status" className="text-sm text-coval-text-muted">
              Loading Slack channels…
            </p>
          ) : !catalog && error ? null : catalog?.binding_status !== "ready" ? (
            <p className="text-sm text-coval-text-muted">
              Connect Slack with organization admin access to configure channel
              notifications.
            </p>
          ) : (
            <>
              <div className="space-y-2">
                <p className="text-sm font-medium text-coval-text">
                  Workspace Slack channels
                </p>
                {workspaceBindings.length === 0 &&
                disconnectedSavedIds.length === 0 ? (
                  <p className="text-sm text-coval-text-muted">
                    No Slack channels are connected to this workspace yet. Add
                    one below.
                  </p>
                ) : (
                  <>
                    {workspaceBindings.map((binding) => (
                      <SlackChannelOption
                        key={binding.channel_id}
                        label={`#${channelsById.get(binding.channel_id)?.name ?? binding.channel_id}`}
                        checked={selectedChannels.has(binding.channel_id)}
                        disabled={saving}
                        onCheckedChange={(next) =>
                          toggleChannelSelection(binding.channel_id, next)
                        }
                      />
                    ))}
                    {disconnectedSavedIds.map((channelId) => {
                      const name = channelsById.get(channelId)?.name
                      return (
                        <SlackChannelOption
                          key={channelId}
                          label={name ? `#${name}` : `Channel ${channelId}`}
                          note="No longer connected to this workspace"
                          checked={selectedChannels.has(channelId)}
                          disabled={saving}
                          onCheckedChange={(next) =>
                            toggleChannelSelection(channelId, next)
                          }
                        />
                      )
                    })}
                  </>
                )}
              </div>

              {notebookReadyAvailable &&
                (availableToConnect.length > 0 || moreChannelsAvailable) && (
                  <div className="space-y-2 border-t border-coval-border pt-3">
                    <p className="text-sm font-medium text-coval-text">
                      Connect another Slack channel
                    </p>
                    {availableToConnect.length > 0 && (
                      <div className="flex items-center gap-2">
                        <Select
                          value={newChannelId}
                          onValueChange={setNewChannelId}
                          disabled={saving}
                        >
                          <SelectTrigger aria-label="Choose Slack channel to connect">
                            <SelectValue placeholder="Select a Slack channel…" />
                          </SelectTrigger>
                          <SelectContent>
                            {availableToConnect.map((ch) => (
                              <SelectItem key={ch.id} value={ch.id}>
                                #{ch.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button
                          variant="secondary"
                          disabled={!newChannelId || !acknowledged || saving}
                          onClick={() => void connectChannel()}
                        >
                          Connect channel
                        </Button>
                      </div>
                    )}
                    {moreChannelsAvailable && (
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-sm text-coval-text-muted">
                          {catalog?.channels.length ?? 0} Slack channels loaded.
                          Load more if your channel is missing.
                        </p>
                        <Button
                          variant="secondary"
                          loading={loadingMore}
                          disabled={saving}
                          onClick={() => void loadMoreChannels()}
                        >
                          Load more Slack channels
                        </Button>
                      </div>
                    )}
                  </div>
                )}

              <label className="flex cursor-pointer items-start gap-3 rounded-md bg-surface-1 p-3 text-sm">
                <Checkbox
                  aria-label="Acknowledge channel audience policy"
                  checked={acknowledged}
                  disabled={saving}
                  onCheckedChange={(checked) =>
                    setAcknowledged(checked === true)
                  }
                />
                <span className="text-coval-text-muted">
                  I understand everyone in the selected Slack channel can read
                  the notebook summary and link Sofia posts.
                </span>
              </label>
            </>
          )}
          {error ? (
            <p role="alert" className="text-sm text-coval-danger-text">
              {error}
            </p>
          ) : null}
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          {catalog?.binding_status === "ready" && (
            <Button
              disabled={saving || loading || loadingMore}
              onClick={() => void saveSeriesSlack()}
            >
              {saving ? "Saving…" : "Save Slack settings"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function SlackChannelOption({
  label,
  note,
  checked,
  disabled,
  onCheckedChange,
}: {
  label: string
  note?: string
  checked: boolean
  disabled: boolean
  onCheckedChange: (checked: boolean) => void
}) {
  return (
    <label className="flex cursor-pointer items-center gap-3 rounded-md border border-coval-border p-2.5 text-sm">
      <Checkbox
        aria-label={`Send to ${label}`}
        checked={checked}
        disabled={disabled}
        onCheckedChange={(next) => onCheckedChange(next === true)}
      />
      <span className="font-medium text-coval-text">{label}</span>
      {note ? <span className="text-coval-text-muted">{note}</span> : null}
    </label>
  )
}
